import {
  MIN_PER_HOUR,
  SEC_PER_MIN,
  SUPPORTED_UNITS,
} from '@/helpers/unit-formatter/unit-formatter.constants';
import { UnitValue } from '@/helpers/unit-formatter/unit-formatter.types';
import {
  fpMultiply,
  secondsToDuration,
  toNumber,
} from '@/helpers/unit-formatter/unit-formatter.utils';

/**
 * Transforms time values from hours to other time units.
 * Supports conversion to minutes (min) and seconds (sec).
 * Uses floating-point safe arithmetic operations for accurate conversions.
 *
 * @param {UnitValue} unitValue - The time value in hours
 * @param {any} unitValue.value - The numeric time value in hours
 * @param {string} [unitValue.units] - The current units (should be 'hrs' or similar)
 * @param {string} [newUnits] - Target time unit ('min', 'sec')
 * @returns {UnitValue} Transformed time value in the target units
 *   - Returns original value if newUnits is not recognized
 *   - Returns original value if input value is null/undefined
 *
 * @example
 * transformHrs({ value: 1, units: 'hrs' }, 'min')
 * // Returns: { value: 60, units: 'min' }
 *
 * @example
 * transformHrs({ value: 2.5, units: 'hrs' }, 'min')
 * // Returns: { value: 150, units: 'min' }
 *
 * @example
 * transformHrs({ value: 1, units: 'hrs' }, 'sec')
 * // Returns: { value: 3600, units: 'sec' }
 */
export const transformHrs = (unitValue: UnitValue, newUnits?: string): UnitValue => {
  const value = toNumber(unitValue.value);
  if (value == null || value == undefined) return unitValue;

  switch (newUnits?.toLowerCase()) {
    case SUPPORTED_UNITS.MIN:
      return {
        value: fpMultiply(value, MIN_PER_HOUR),
        units: newUnits,
      };
    case SUPPORTED_UNITS.SEC:
      return {
        value: fpMultiply(value, SEC_PER_MIN * MIN_PER_HOUR),
        units: newUnits,
      };
    case SUPPORTED_UNITS.TIMER:
    case SUPPORTED_UNITS.DURATION:
      return {
        value: secondsToDuration(fpMultiply(value, SEC_PER_MIN * MIN_PER_HOUR)),
        units: '',
      };
    default:
      return unitValue;
  }
};
